import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast, useTRPC } from '@barev/web';
import { useState } from 'react';
import { errText, useT, type BoKey } from '../i18n.tsx';
import { Dialog, Field } from '../components/kit.tsx';

export type SubMode = 'gift' | 'sell' | 'extend' | 'cancel' | 'plan' | 'refund';
export interface SubTarget {
  id: string;
  plan: 'BAREV_PLUS' | 'ELITE';
  startsAt: Date;
  endsAt: Date;
  amount: number | null;
  refundAmount: number | null;
}
type Plan = 'BAREV_PLUS' | 'ELITE';
type PayMethod = 'cash' | 'transfer' | 'card' | 'link';

const DAY = 86_400_000;
const PERIODS = [
  { days: 30, key: 'dlg.period1' },
  { days: 90, key: 'dlg.period3' },
  { days: 180, key: 'dlg.period6' },
  { days: 365, key: 'dlg.period12' },
] as const;

const TITLE: Record<SubMode, BoKey> = { gift: 'dlg.giftTitle', sell: 'dlg.sellTitle', extend: 'dlg.extendTitle', cancel: 'dlg.cancelTitle', plan: 'dlg.planTitle', refund: 'dlg.refundTitle' };
const CONFIRM: Record<SubMode, BoKey> = { gift: 'dlg.giftConfirm', sell: 'dlg.sellConfirm', extend: 'dlg.extendConfirm', cancel: 'dlg.cancelConfirm', plan: 'dlg.planConfirm', refund: 'dlg.refundConfirm' };
const DONE: Record<SubMode, BoKey> = { gift: 'done.gift', sell: 'done.sell', extend: 'done.extend', cancel: 'done.cancel', plan: 'done.changePlan', refund: 'done.refund' };

export function SubDialog({ clientId, mode, sub, current, onClose }: { clientId: string; mode: SubMode; sub?: SubTarget; current?: SubTarget; onClose: () => void }) {
  const { t, amd, date } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();

  const [plan, setPlan] = useState<Plan>(mode === 'plan' && sub ? (sub.plan === 'ELITE' ? 'BAREV_PLUS' : 'ELITE') : (current?.plan ?? 'BAREV_PLUS'));
  const [days, setDays] = useState(30);
  const [custom, setCustom] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PayMethod>('cash');
  const [note, setNote] = useState('');
  const [delta, setDelta] = useState('30');
  const [refund, setRefund] = useState(sub?.amount ? String(sub.amount - (sub.refundAmount ?? 0)) : '');
  const [cancelToo, setCancelToo] = useState(true);

  const opts = {
    onSuccess: () => {
      toast(t(DONE[mode]));
      qc.invalidateQueries({ queryKey: trpc.bo.clients.get.queryKey({ id: clientId }) });
      qc.invalidateQueries({ queryKey: trpc.bo.clients.list.queryKey() });
      qc.invalidateQueries({ queryKey: trpc.bo.dashboard.overview.queryKey() });
      onClose();
    },
    onError: (e: unknown) => toast(errText(t, e), 'error'),
  };
  const gift = useMutation(trpc.bo.clients.gift.mutationOptions(opts));
  const sell = useMutation(trpc.bo.clients.sell.mutationOptions(opts));
  const extend = useMutation(trpc.bo.clients.extend.mutationOptions(opts));
  const cancel = useMutation(trpc.bo.clients.cancel.mutationOptions(opts));
  const changePlan = useMutation(trpc.bo.clients.changePlan.mutationOptions(opts));
  const refundM = useMutation(trpc.bo.clients.refund.mutationOptions(opts));
  const pending = [gift, sell, extend, cancel, changePlan, refundM].some((m) => m.isPending);

  // новая подписка того же тарифа встаёт в очередь за текущей
  const queuedAfter = (mode === 'gift' || mode === 'sell') && current && current.plan === plan ? current.endsAt : null;
  const deltaN = Number(delta) || 0;
  const amountN = Number(amount.replace(/\s/g, ''));
  const refundN = Number(refund.replace(/\s/g, ''));

  const valid =
    mode === 'gift'
      ? days > 0
      : mode === 'sell'
        ? days > 0 && amount !== '' && amountN >= 0 && method !== 'link'
        : mode === 'extend'
          ? deltaN !== 0
          : mode === 'refund'
            ? refundN > 0
            : true;

  const submit = () => {
    const base = { id: clientId };
    if (mode === 'gift') gift.mutate({ ...base, plan, days, note: note || undefined });
    if (mode === 'sell') sell.mutate({ ...base, plan, days, amount: amountN, payMethod: method, note: note || undefined });
    if (mode === 'extend' && sub) extend.mutate({ ...base, subscriptionId: sub.id, days: deltaN });
    if (mode === 'cancel' && sub) cancel.mutate({ ...base, subscriptionId: sub.id, reason: note || undefined });
    if (mode === 'plan' && sub) changePlan.mutate({ ...base, subscriptionId: sub.id, plan });
    if (mode === 'refund' && sub) refundM.mutate({ ...base, subscriptionId: sub.id, amount: refundN, cancel: cancelToo });
  };

  const planPicker = (
    <Field label={t('dlg.plan')}>
      <div className="bo-pills">
        {(['BAREV_PLUS', 'ELITE'] as const).map((p) => (
          <button key={p} type="button" className="bo-pill" aria-pressed={plan === p} onClick={() => setPlan(p)} disabled={mode === 'plan' && sub?.plan === p}>
            {t(`plan.${p}`)}
          </button>
        ))}
      </div>
    </Field>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(TITLE[mode])}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className={`bo-btn ${mode === 'cancel' || mode === 'refund' ? 'bo-btn--accent' : ''}`} disabled={!valid || pending} onClick={submit}>
            {t(CONFIRM[mode])}
          </button>
        </>
      }
    >
      {(mode === 'gift' || mode === 'sell') && (
        <>
          {mode === 'gift' && <p className="bo-hint">{t('dlg.giftHint')}</p>}
          {planPicker}
          <Field label={t('dlg.period')}>
            <div className="bo-pills">
              {PERIODS.map((p) => (
                <button
                  key={p.days}
                  type="button"
                  className="bo-pill"
                  aria-pressed={!custom && days === p.days}
                  onClick={() => {
                    setCustom(false);
                    setDays(p.days);
                  }}
                >
                  {t(p.key)}
                </button>
              ))}
              <button type="button" className="bo-pill" aria-pressed={custom} onClick={() => setCustom(true)}>
                {t('dlg.periodCustom')}
              </button>
            </div>
          </Field>
          {custom && (
            <Field label={t('dlg.days')}>
              <input className="bo-input" type="number" min={1} max={3660} value={days} onChange={(e) => setDays(Math.max(0, Number(e.target.value)))} />
            </Field>
          )}
          {queuedAfter && <p className="bo-hint">{t('card.upcoming', { date: date(queuedAfter) })}</p>}
          {mode === 'sell' && (
            <>
              <div className="bo-row2">
                <Field label={t('dlg.amount')}>
                  <input className="bo-input" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d\s]/g, ''))} />
                </Field>
                <Field label={t('dlg.method')}>
                  <select className="bo-select" value={method} onChange={(e) => setMethod(e.target.value as PayMethod)}>
                    {(['cash', 'transfer', 'card', 'link'] as const).map((m) => (
                      <option key={m} value={m}>
                        {t(`pay.${m}`)}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              {method === 'link' && <p className="bo-hint">{t('dlg.linkSoon')}</p>}
            </>
          )}
          <Field label={t('dlg.note')}>
            <input className="bo-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </Field>
        </>
      )}

      {mode === 'extend' && sub && (
        <>
          <Field label={t('dlg.days')} hint={t('dlg.extendHint')}>
            <input className="bo-input" type="number" value={delta} onChange={(e) => setDelta(e.target.value)} autoFocus />
          </Field>
          <div className="bo-pills">
            {[7, 30, 90].map((n) => (
              <button key={n} type="button" className="bo-pill" onClick={() => setDelta(String(n))} aria-pressed={deltaN === n}>
                +{n}
              </button>
            ))}
          </div>
          {deltaN !== 0 && <p style={{ margin: 0, fontWeight: 600 }}>{t('dlg.newEnd', { date: date(new Date(sub.endsAt.getTime() + deltaN * DAY)) })}</p>}
        </>
      )}

      {mode === 'plan' && planPicker}

      {mode === 'cancel' && (
        <>
          <p className="bo-hint">{t('dlg.cancelHint')}</p>
          <Field label={t('dlg.reason')}>
            <input className="bo-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} autoFocus />
          </Field>
        </>
      )}

      {mode === 'refund' && sub && (
        <>
          <p className="bo-hint">{t('dlg.refundPaid', { sum: amd(sub.amount ?? 0), back: amd(sub.refundAmount ?? 0) })}</p>
          <Field label={t('dlg.amount')}>
            <input className="bo-input" inputMode="numeric" value={refund} onChange={(e) => setRefund(e.target.value.replace(/[^\d\s]/g, ''))} autoFocus />
          </Field>
          <label className="bo-check">
            <input type="checkbox" checked={cancelToo} onChange={(e) => setCancelToo(e.target.checked)} />
            {t('dlg.refundCancel')}
          </label>
        </>
      )}
    </Dialog>
  );
}
